import { GetTelemetryHistoryResponse } from '@eppendorf/vnls-device-service-utils';
import { useMemo } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  DotProps,
  ReferenceDot,
  Curve,
  type CurveProps,
} from 'recharts';

import {
  formatDecimal,
  getSampledTicks,
  liveInterpolation,
} from '$shared/utils/charts.utils';
import { getLocalizedMonthAndDay, getLocalizedTime } from '$shared/utils/date.utils';

import { CustomTooltipV2 } from '$features/monitoring/history-charts-v2/custom-tooltip-v2';
import { useHistoryChartsConfig } from '$features/monitoring/history-charts-v2/useHistoryChartsConfig';

export interface HistoryChartV2Props {
  data: GetTelemetryHistoryResponse;
  showLiveAnimation?: boolean;
  /** When true, setpoint and threshold lines are hidden */
  hideSetpoints?: boolean;
  /** Override fractional digits for Y-axis and tooltip values */
  fractionalDigits?: number;
  syncId?: string;
  /** Match timestamps rather than indices, preferring the new interval at a boundary. */
  syncByTimestamp?: boolean;
}

function syncTooltipByTimestamp(
  ticks: ReadonlyArray<{ value: number }>,
  { activeLabel }: { activeLabel?: number },
): number {
  return ticks.findLastIndex(({ value }) => value === activeLabel);
}

function TimestampSyncCursor({
  points,
  payloadIndex,
}: Pick<CurveProps, 'points'> & { payloadIndex?: number }) {
  // Recharts otherwise places an unmatched synchronized cursor at the chart origin.
  if (payloadIndex === undefined || payloadIndex < 0) return null;
  return (
    <Curve
      points={points}
      stroke="#ccc"
      pointerEvents="none"
      className="recharts-tooltip-cursor"
      data-testid="history-chart-cursor"
    />
  );
}

function CustomizedDot(props: DotProps) {
  const { chartColors } = useHistoryChartsConfig();
  const { cx, cy } = props;
  return (
    <svg data-testid="live-animation-dot">
      <circle
        className="live-animate live-chart-outer-circle"
        cx={cx}
        cy={cy}
        strokeWidth={0}
        fill={chartColors.processValueColor}
      />
      <circle
        className="live-animate live-chart-inner-circle"
        cx={cx}
        cy={cy}
        strokeWidth={0}
        fill={chartColors.processValueColor}
      />
      <circle
        cx={cx}
        cy={cy}
        r={7}
        strokeWidth={0}
        fill={chartColors.processValueColor}
      />
    </svg>
  );
}

function useDataTimeRange(data: GetTelemetryHistoryResponse['historyData']): {
  from: number;
  to: number;
} {
  const range = useMemo(() => {
    const first = data.at(0);
    const last = data.at(-1);

    if (!first || !last) {
      return { from: Date.now(), to: Date.now() };
    }

    return { from: first.timestamp, to: last.timestamp };
  }, [data]);

  return range;
}

function useTimeRangeDomainExtension(from: number, to: number): number {
  return Math.min((to - from) * 0.03, 100_000);
}

const NON_MINUTE_TICK_COUNT = 5;

function defaultTickFormatter(val: number): string {
  return `${getLocalizedMonthAndDay(val)} ${getLocalizedTime(val)}`;
}

export function HistoryChartV2({
  data,
  showLiveAnimation,
  hideSetpoints,
  fractionalDigits,
  syncId = 'anyId',
  syncByTimestamp = false,
}: HistoryChartV2Props) {
  const { historyData, unit } = data;
  const { showThresholdRefs, chartColors } = useHistoryChartsConfig();
  const effectiveShowThresholdRefs = hideSetpoints ? false : showThresholdRefs;
  const activeDotCSS = { strokeWidth: 2, r: 7 };
  const { from, to } = useDataTimeRange(historyData);
  const shouldUseMinuteTicks = showLiveAnimation || to - from <= 1 * 60 * 60 * 1000; // 1 hour
  const xAxisDomainExtension = useTimeRangeDomainExtension(from, to);
  const ticks = shouldUseMinuteTicks
    ? liveInterpolation(to, 14)
    : getSampledTicks(
        historyData.map((bucket) => bucket.timestamp),
        NON_MINUTE_TICK_COUNT,
      );
  const tickFormatter = shouldUseMinuteTicks ? getLocalizedTime : defaultTickFormatter;

  return (
    <ResponsiveContainer width="98%" height={300}>
      <AreaChart
        syncId={syncId}
        syncMethod={syncByTimestamp ? syncTooltipByTimestamp : 'index'}
        // Width and height needed for unit-testing only.
        // They are ignored by ResponsiveContainer in production.
        width={100}
        height={100}
        data={historyData}
        margin={{
          top: 10,
          right: 5,
          left: 0, // needs to be 0, otherwise large y-axis values are not fully printed
          bottom: 0,
        }}
        dataKey="avg"
      >
        <CartesianGrid vertical={false} />

        <XAxis
          dataKey="timestamp"
          domain={['dataMin', `dataMax + ${xAxisDomainExtension}`]}
          type="number"
          allowDataOverflow
          axisLine={false}
          tickMargin={10}
          padding={{
            left: 5,
          }}
          ticks={ticks}
          tickFormatter={tickFormatter}
        />
        <YAxis
          padding={{
            top: 0,
          }}
          axisLine={false}
          tickLine={false}
          tickFormatter={
            fractionalDigits !== undefined
              ? (val: number) => formatDecimal(val, fractionalDigits)
              : undefined
          }
        />

        <defs>
          <linearGradient id="colorProcessValue" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3B81F5" stopOpacity={0.2} />
            <stop offset="100%" stopColor="#3B81F5" stopOpacity={0} />
          </linearGradient>
        </defs>

        <Tooltip
          cursor={syncByTimestamp ? <TimestampSyncCursor /> : true}
          content={
            <CustomTooltipV2 unit={unit} fractionalDigits={fractionalDigits ?? 1} />
          }
        />

        {showLiveAnimation && (
          <ReferenceDot
            x={data?.historyData.at(-1)?.timestamp}
            y={data?.historyData.at(-1)?.avg ?? undefined}
            shape={<CustomizedDot />}
          />
        )}

        {effectiveShowThresholdRefs && (
          <>
            <Area
              type="linear"
              dataKey="setPoint"
              stroke={chartColors.setPointColor}
              strokeWidth={2}
              baseLine={8}
              fill="none"
              activeDot={activeDotCSS}
            />
            <Area
              type="linear"
              dataKey="alertHigh"
              stroke={chartColors.alertHighLowColor}
              strokeWidth={2}
              baseLine={8}
              fill="none"
              activeDot={activeDotCSS}
            />
            <Area
              type="linear"
              dataKey="alertLow"
              stroke={chartColors.alertHighLowColor}
              strokeWidth={2}
              baseLine={8}
              fill="none"
              activeDot={activeDotCSS}
            />
          </>
        )}

        <Area
          type="linear"
          dataKey="avg"
          stroke={chartColors.processValueColor}
          strokeWidth={2}
          baseLine={8}
          fill="url(#colorProcessValue)"
          baseValue="dataMin"
          activeDot={activeDotCSS}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}
